<script setup>
import {computed} from 'vue'
import { useProjectStore } from '@/stores/project'
import { useGeoStore } from '@/stores/geo'
import { useBoundaryStore } from '@/stores/boundary'
import {useRouter, useRoute} from 'vue-router'
import {FolderPlus, Eye, Trash2, Upload, Settings} from 'lucide-vue-next'
import {bbox} from '@turf/turf'

import {DialogConfirm} from '@/components/dialogs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

const props = defineProps({
  project: Object
})

const projectStore = useProjectStore()

const geoStore = useGeoStore()

const boundaryStore = useBoundaryStore()
const router = useRouter()
const route = useRoute()

const map = computed(() => geoStore.map)
const isSelected = computed(() => parseInt(route.params.project_id) === props.project.project_id)

const routeParams = computed(() => ({
  org_id: props.project.organization_id,
  project_id: props.project.project_id
}))

const hasGeometry = computed(() => {
  const boundaries = boundaryStore.boundaries
  if (!boundaries?.features) return false
  const boundary = boundaries.features.find(
    f => f.properties.project_id === props.project.project_id
  )
  return !!boundary?.geometry
})

const zoomOnBoundary = async () => {
  try {
    const boundary = await boundaryStore.fetchProjectBoundary(props.project.project_id)
    if (!boundary || !boundary.geometry) return
    map.value.fitBounds(bbox(boundary))
  } catch (error) {
    console.error('Failed to fetch project boundary', error)
  }
}

const goTo = (name) => {
  router.push({ name, params: routeParams.value })
}

const deleteProject = async () => {
  await projectStore.deleteProject(props.project)
  await projectStore.fetchProjects()
  if (isSelected.value) {
    router.push({ name: 'Dashboard' })
  }
}

</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <slot name="trigger" />
    </DropdownMenuTrigger>
    <DropdownMenuContent class="w-48">
      <DropdownMenuGroup>
        <div :title="!hasGeometry ? 'Add a location to zoom to this project' : undefined">
          <DropdownMenuItem @click="zoomOnBoundary" class="cursor-pointer" :disabled="!hasGeometry">
            <Eye class="w-4 h-4 mr-2" />
            <span>View on Map</span>
          </DropdownMenuItem>
        </div>
        <DropdownMenuItem @click="goTo('ProjectImport')" class="cursor-pointer">
          <Upload class="w-4 h-4 mr-2" />
          <span>Import Data</span>
        </DropdownMenuItem>
        <DropdownMenuItem @click="goTo('ProjectCreate')" class="cursor-pointer">
          <FolderPlus class="w-4 h-4 mr-2" />
          <span>New Sub-project</span>
        </DropdownMenuItem>
        <DropdownMenuItem @click="goTo('ProjectSettings')" class="cursor-pointer">
          <Settings class="w-4 h-4 mr-2" />
          <span>Settings</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DialogConfirm
          @confirm="deleteProject"
          :title="'Delete ' + project.project_name"
          description="Are you sure you want to delete this project? This action cannot be undone."
        >
          <DropdownMenuItem class="cursor-pointer text-red-600 focus:text-red-600" @select.prevent>
            <Trash2 class="w-4 h-4 mr-2" />
            <span>Delete</span>
          </DropdownMenuItem>
        </DialogConfirm>
      </DropdownMenuGroup>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
